import { useEffect, useRef, useState } from 'react';
import { organisationErrorMessage } from '../../organisations/lib/errorMessage';

/**
 * One request's answer, keyed by what was asked for: `load()` runs while
 * `enabled` whenever `key` changes, and only the answer for the current key
 * is returned, so a slow answer for a scope the teacher has left never
 * shows, and until the current one arrives the data reads as loading
 * (never briefly as empty). `load` is read at request time, so it need not
 * be memoised.
 */
const useKeyedRequest = ({ key, enabled, load, errorText }) => {
  const [result, setResult] = useState({ key: null, data: null, error: '' });
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    // Through a promise, so a load that throws is an error shown, not a crash.
    Promise.resolve().then(() => loadRef.current())
      .then((data) => { if (!cancelled) setResult({ key, data, error: '' }); })
      .catch((err) => {
        if (!cancelled) setResult({ key, data: null, error: organisationErrorMessage(err, errorText) });
      });
    return () => { cancelled = true; };
  }, [enabled, key, errorText]);

  const current = result.key === key ? result : { data: null, error: '' };
  return {
    data: current.data,
    error: current.error,
    loading: Boolean(enabled) && result.key !== key,
  };
};

export default useKeyedRequest;
